import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import {
  POD_SESSION_LABELS,
  POD_STATE_LABELS,
  formatNextBlock,
  formatSeconds,
} from "../../texts/podDialer"
import { podsApi } from "../axios"

const POLL_MS = 3000

function percent(value) {
  return value == null ? "—" : `${(value * 100).toFixed(1)}%`
}

function goalCell(done, target, monthDone, monthGoal) {
  if (monthGoal == null) return "—"
  return `${done} / ${
    target ?? "—"
  } this week · ${monthDone} / ${monthGoal} month`
}

function clientStatus(row) {
  if (row.goal.met) {
    return row.goal.reason === "month" ? "Month goal met" : "Week goal met"
  }
  if (!row.numbers) return "No numbers"
  if (!row.pending_leads) return "Out of leads"
  return "Dialing"
}

/** Live view of the pod: agents, lines, drop rate, client goals; start/pause/end. */
export default function PodLiveBoard({ podId }) {
  const [board, setBoard] = useState(null)
  const [error, setError] = useState("")

  const load = useCallback(
    () =>
      podsApi
        .live(podId)
        .then(({ data }) => setBoard(data))
        .catch(() => setError("Could not load the live board.")),
    [podId]
  )

  useEffect(() => {
    load()
    const timer = window.setInterval(load, POLL_MS)
    return () => window.clearInterval(timer)
  }, [load])

  function act(action) {
    podsApi
      .sessionAction(podId, action, 1)
      .then(load)
      .catch(() => setError("Could not change the session."))
  }

  if (!board) return null
  const live = board.live || {}
  const session = live.session
  const dials = live.dials || {}

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Column gap={2}>
        <Row
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Column gap={0.25}>
            <Typography variant="h6" fontWeight={700}>
              Live
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {session
                ? POD_SESSION_LABELS[session.status] || session.status
                : formatNextBlock(board.next_block_at)}
            </Typography>
          </Column>
          <Row sx={{ gap: 1 }}>
            {!session ? (
              <Button variant="outlined" onClick={() => act("start")}>
                Start 1 hour now
              </Button>
            ) : null}
            {session?.status === "active" ? (
              <Button variant="outlined" onClick={() => act("pause")}>
                Pause dialing
              </Button>
            ) : null}
            {session?.status === "paused" ? (
              <Button variant="contained" onClick={() => act("resume")}>
                Resume
              </Button>
            ) : null}
            {session && session.status !== "ending" ? (
              <Button color="error" onClick={() => act("end")}>
                End session
              </Button>
            ) : null}
          </Row>
        </Row>
        {error ? (
          <Alert severity="error" onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {session ? (
          <Row sx={{ gap: 3, flexWrap: "wrap" }}>
            <Stat label="Dials" value={dials.total ?? 0} />
            <Stat label="Ringing" value={dials.ringing ?? 0} />
            <Stat label="Live answers" value={dials.live ?? 0} />
            <Stat label="Answer rate" value={percent(live.answer_rate)} />
            <Stat label="Lines per agent" value={live.lines_per_agent ?? 0} />
            <Stat
              label="Drop rate (30 days)"
              value={percent(live.drop_rate_30d)}
            />
          </Row>
        ) : null}
        {session && live.agents?.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>State</TableCell>
                <TableCell>For</TableCell>
                <TableCell>On with</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {live.agents.map((agent) => (
                <TableRow key={agent.user.id}>
                  <TableCell>{agent.user.name}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={POD_STATE_LABELS[agent.state] || agent.state}
                    />
                  </TableCell>
                  <TableCell>{formatSeconds(agent.seconds_in_state)}</TableCell>
                  <TableCell>{agent.lead_name || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Client</TableCell>
              <TableCell>Bookings</TableCell>
              <TableCell>Hours</TableCell>
              <TableCell align="right">Voicemails</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(board.clients || []).map((row) => (
              <TableRow key={row.campaign_id}>
                <TableCell>
                  {row.client_has_campaigns
                    ? `${row.client} — ${row.campaign}`
                    : row.client}
                </TableCell>
                <TableCell>
                  {goalCell(
                    row.goal.bookings_week,
                    row.goal.bookings_week_target,
                    row.goal.bookings_month,
                    row.goal_bookings
                  )}
                </TableCell>
                <TableCell>
                  {goalCell(
                    row.goal.hours_week,
                    row.goal.hours_week_target,
                    row.goal.hours_month,
                    row.goal_hours
                  )}
                </TableCell>
                <TableCell align="right">
                  {row.unhandled_voicemails || 0}
                </TableCell>
                <TableCell>{clientStatus(row)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Column>
    </Paper>
  )
}

function Stat({ label, value }) {
  return (
    <Column gap={0.25}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700}>
        {value}
      </Typography>
    </Column>
  )
}
